import Plan from 'types/Plan';
import Texture from '@alltrails/shared/denali/components/Texture';
import Typography from '@alltrails/shared/denali/components/Typography';
import { UserId } from '@alltrails/shared/types/User';
import PrimaryLink from 'components/LandingPages/PrimaryLink';
import NoSsr from '@material-ui/core/NoSsr';
import { lazy, Suspense } from 'react';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useUser from 'hooks/useUser';
import usePlusFeatures from 'components/LandingPages/hooks/usePlusFeatures';
import Coupon from 'types/Coupon';
import heroDesktopJpg from './images/hero-2880w.jpg';
import heroDesktopWebp from './images/hero-2880w.webp';
import heroDesktopAvif from './images/hero-2880w.avif';
import logosDesktop from './images/logosDesktop.svg';
import logosMobile from './images/logosMobile.svg';
import * as styles from './PetSmartLandingPage.module.scss';

// Desktop + Mobile: https://www.figma.com/file/P4IkeqfAVc6FYxlyPS7Yov/F-2023_Partnerships?type=design&node-id=2449-97806&t=QuZyph2Uqqp4c2Io-0

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

type MerchLandingPageProps = {
  coupon?: Coupon;
  planTypes: Plan[];
  userId?: UserId;
};

function getTrialMonths(coupon: Coupon, trialPreview: string): string {
  const couponTrial = coupon?.freeTrialDuration;
  if (coupon) {
    const durationData = couponTrial.split(' ');
    return durationData[0];
  }

  if (trialPreview === '6monthpetsmart') {
    return '6';
  }
  if (trialPreview === '3monthpetsmart') {
    return '3';
  }

  return '';
}

export default function PetSmartLandingPage({ planTypes, userId, coupon }: MerchLandingPageProps) {
  const user = useUser();
  const plan = planTypes[0];

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const trialParam = params.get('trial');
  const trialMonths = getTrialMonths(coupon, trialParam);

  const search = params.toString();

  const to = user || userId ? `/plus/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;

  const features = usePlusFeatures();

  const title = (
    <Typography component="div">
      <p>{`Get ${trialMonths} Months of AllTrails+`}</p>
    </Typography>
  );

  const subhead = (
    <div>
      <Typography component="div">
        <p>
          Do more with your furry BFF - use AllTrails+ to save time, be prepared, and never miss a turn.
          {trialParam && ' Check your inbox for your promo code.'}
        </p>
      </Typography>
    </div>
  );

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.hero}>
          <picture className={styles.picture}>
            <source srcSet={heroDesktopAvif} type="image/avif" />
            <source srcSet={heroDesktopWebp} type="image/webp" />
            <img alt="Golden Retriever with owner on a forest trail" src={heroDesktopJpg} />
          </picture>
        </div>
        <div className={styles.content}>
          <div className={styles.copy}>
            <div className={styles.logoDesktop}>
              <img src={logosDesktop} alt="AllTrails + PetSmart logos" />
            </div>
            <div className={styles.logoMobile}>
              <img src={logosMobile} alt="AllTrails + PetSmart logos" />
            </div>
            <div>
              <Typography className={styles.subLogo} variant="text300bold">
                <p>Exclusive for PetSmart Treats members</p>
              </Typography>
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.subhead}>{subhead}</div>
            <div className={styles.button}>
              <PrimaryLink
                buttonText="Get Started"
                plan={plan}
                coupon={coupon}
                to={to}
                userId={userId || user?.id}
                desktopVariant="elevated"
                mobileVariant="primary"
              />
              <Typography className={styles.cancel} component="div" mb="8" variant="text100">
                <p>Cancel anytime.</p>
              </Typography>
            </div>
            <Typography className={styles.lottie} component="div" mb="8">
              <NoSsr>
                <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                  <ChevronDownBouncing />
                </Suspense>
              </NoSsr>
            </Typography>
          </div>
          <ProFeatureComparisonTable features={features} />
        </div>
      </div>
      <Texture variant="plus" className={styles.texture} />
    </>
  );
}
